import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Cpu, Zap, Globe, Award, ArrowRight } from 'lucide-react';

const PHOTO_BW = '/calvin-2.png';
const PHOTO_BRANDED = '/calvin-3.png';
const ICON_URL = 'https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/1oy2wqev_ICON.png';

const pillars = [
  { icon: Globe, label: 'Solo Founder Model', sub: 'One vision, zero committees' },
  { icon: Cpu, label: 'AI-Augmented Ops', sub: 'Lean, fast, infinitely scalable' },
  { icon: Zap, label: 'Product Is Live', sub: 'Built, documented, ready to deploy' },
  { icon: Award, label: 'First-Mover Position', sub: 'No bank-owned eWallet like this exists' },
];

const timeline = [
  { year: '2020', label: 'Problem Identified', desc: 'Watched community banks lose billions to Zelle while earning $0 per transaction.' },
  { year: '2022', label: 'Architecture Built', desc: 'Designed the sovereign eWallet stack — FedNow-native, white-label, bank-owned from the ground up.' },
  { year: '2024', label: 'Product Goes Live', desc: 'NexGenPay eWallet fully built, documented, BSA/AML compliant, and ready for bank deployment.' },
  { year: '2025', label: 'Scale Begins', desc: 'Active partnerships, seed round open, targeting 40+ bank deployments in Year 1.' },
];

export default function FounderSection() {
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [tlRevealed, setTlRevealed] = useState(false);
  const [activeYear, setActiveYear] = useState(-1);

  useEffect(() => {
    const obs1 = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setRevealed(true); obs1.disconnect(); } },
      { threshold: 0.08 }
    );
    const obs2 = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setTlRevealed(true);
          timeline.forEach((_, i) => setTimeout(() => setActiveYear(i), i * 350));
          obs2.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs1.observe(sectionRef.current);
    if (timelineRef.current) obs2.observe(timelineRef.current);
    return () => { obs1.disconnect(); obs2.disconnect(); };
  }, []);

  return (
    <section
      id="about"
      data-testid="founder-section"
      ref={sectionRef}
      className="relative bg-black"
      style={{ scrollMarginTop: '80px' }}
    >
      {/* ── TOP GLOW ── */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(0,255,0,0.06), transparent 70%)' }} />

      {/* ══════════════════════════ MAIN BIO ROW ══════════════════════════ */}
      <div className="max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* ── LEFT: PHOTO STACK ── */}
          <div
            className="relative"
            style={{
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateX(0)' : 'translateX(-50px)',
              transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            {/* Primary B&W executive photo */}
            <div className="relative" style={{ zIndex: 2 }}>
              {/* Outer frame with corner accents */}
              <div className="relative border border-[#1A1A1A] overflow-hidden group"
                style={{ boxShadow: '0 0 60px rgba(0,255,0,0.08)' }}>

                {/* Corner accents */}
                {[
                  'top-0 left-0 border-t-2 border-l-2',
                  'top-0 right-0 border-t-2 border-r-2',
                  'bottom-0 left-0 border-b-2 border-l-2',
                  'bottom-0 right-0 border-b-2 border-r-2',
                ].map((cls, i) => (
                  <div key={i} className={`absolute w-6 h-6 border-[#00FF00] z-10 ${cls}`} />
                ))}

                {/* Green scan line on hover */}
                <div className="absolute top-0 left-0 right-0 h-[2px] z-20 opacity-0 group-hover:opacity-100"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #00FF00, transparent)',
                    animation: 'scan 2s linear infinite',
                  }} />

                {/* Photo with green overlay tint on hover */}
                <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                  <img
                    src={PHOTO_BW}
                    alt="Calvin Francis — Founder & CEO"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    style={{ filter: 'contrast(1.1) brightness(0.95)' }}
                  />
                  {/* Neon green gradient overlay */}
                  <div className="absolute inset-0 transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                    style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(0,255,0,0.12) 100%)' }} />
                  {/* Bottom ID tag */}
                  <div className="absolute bottom-0 left-0 right-0 p-5"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.95) 60%, transparent)' }}>
                    <div className="font-mono text-[10px] text-[#00FF00] uppercase tracking-widest mb-1">
                      Founder & CEO
                    </div>
                    <div className="text-white font-black text-xl uppercase"
                      style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}>
                      Calvin Francis
                    </div>
                    <div className="font-mono text-[10px] text-[#A3A3A3] uppercase tracking-widest mt-1">
                      Next Generation Payments
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating branded accent photo */}
            <div
              className="absolute -bottom-8 -right-6 w-44 border-2 border-[#00FF00] overflow-hidden"
              style={{
                zIndex: 3,
                boxShadow: '0 0 30px rgba(0,255,0,0.3)',
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateY(0) rotate(2deg)' : 'translateY(20px)',
                transition: 'all 1s ease 0.4s',
              }}
            >
              <img
                src={PHOTO_BRANDED}
                alt="Calvin Francis — NexGenPay"
                className="w-full object-cover"
                style={{ aspectRatio: '3/4', objectPosition: 'top' }}
              />
              <div className="absolute bottom-0 left-0 right-0 py-2 px-3"
                style={{ background: 'rgba(0,0,0,0.9)' }}>
                <div className="flex items-center gap-1.5">
                  <img src={ICON_URL} alt="NX" className="w-4 h-4 object-contain" />
                  <span className="text-[#00FF00] font-mono text-[9px] uppercase tracking-widest">NexGenPay</span>
                </div>
              </div>
            </div>

            {/* Decorative grid behind photo */}
            <div className="absolute -inset-4 -z-10"
              style={{
                backgroundImage: 'linear-gradient(rgba(0,255,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,0,0.04) 1px, transparent 1px)',
                backgroundSize: '30px 30px',
              }} />
          </div>

          {/* ── RIGHT: BIO CONTENT ── */}
          <div
            style={{
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateX(0)' : 'translateX(50px)',
              transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1) 0.15s',
            }}
          >
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-[2px] bg-[#00FF00]" />
              <span className="text-[#00FF00] font-mono text-[11px] uppercase tracking-[0.4em]">The Founder</span>
            </div>

            {/* Headline */}
            <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-none mb-2"
              style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}>
              One Founder.
            </h2>
            <h2 className="text-4xl sm:text-5xl font-black uppercase leading-none mb-8"
              style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em', color: '#00FF00', textShadow: '0 0 30px rgba(0,255,0,0.4)' }}>
              One Vision.
            </h2>

            {/* Bio paragraphs */}
            <div className="flex flex-col gap-5 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <p className="text-[#A3A3A3] text-[15px] leading-relaxed">
                Calvin Francis built NexGenPay from the ground up — a solo founder operating at the intersection of community banking and financial technology. He watched firsthand as community banks processed billions in transactions through Zelle and walked away with nothing. He decided to build the infrastructure that would change that.
              </p>
              <p className="text-[#A3A3A3] text-[15px] leading-relaxed">
                With deep expertise in fintech positioning and community bank partnerships, Calvin architected a platform that doesn't compete with banks — it hands them back control. The eWallet is white-label, FedNow-native, and fully bank-owned. Built. Documented. Ready to deploy.
              </p>
              <p className="text-[#A3A3A3] text-[15px] leading-relaxed">
                Operating through an AI-augmented model, NexGenPay moves at startup speed while delivering enterprise-grade infrastructure — a solo-founder advantage that turns lean into lethal. Every line of code, every partnership conversation, every investor deck was built with one mission: <span className="text-white font-semibold">make bank sovereignty possible.</span>
              </p>
            </div>

            {/* Vision quote */}
            <div className="relative mb-8 pl-6 border-l-2 border-[#00FF00]"
              style={{ borderLeftColor: '#00FF00' }}>
              <div className="text-[#00FF00] text-4xl font-black leading-none mb-2 opacity-40"
                style={{ fontFamily: 'Georgia, serif' }}>
                "
              </div>
              <p className="text-white text-base font-medium leading-relaxed italic"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
                Banks must reclaim the digital transaction layer before the window closes and fintechs lock in permanent structural advantage. I built NexGenPay so they could fight back.
              </p>
              <div className="mt-3 font-mono text-[10px] text-[#00FF00] uppercase tracking-widest">
                — Calvin Francis, Founder & CEO
              </div>
            </div>

            {/* Pillars */}
            <div className="grid grid-cols-2 gap-3">
              {pillars.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.label}
                    data-testid={`founder-pillar-${i}`}
                    className="border border-[#1A1A1A] p-4 group hover:border-[#00FF00] transition-all duration-300"
                    style={{
                      background: '#0A0A0A',
                      opacity: revealed ? 1 : 0,
                      transform: revealed ? 'translateY(0)' : 'translateY(20px)',
                      transition: `all 0.6s ease ${0.5 + i * 0.1}s`,
                    }}
                  >
                    <Icon size={16} color="#00FF00" className="mb-2" />
                    <div className="text-white text-[11px] font-bold uppercase tracking-wide"
                      style={{ fontFamily: 'Unbounded, sans-serif', lineHeight: '1.3' }}>
                      {p.label}
                    </div>
                    <div className="text-[#A3A3A3] text-[11px] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {p.sub}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════ FULL-WIDTH VISION STRIP ══════════════════════════ */}
      <div
        className="relative overflow-hidden my-16"
        style={{
          background: 'linear-gradient(135deg, #000 0%, #050505 40%, #000 100%)',
          borderTop: '1px solid #1A1A1A',
          borderBottom: '1px solid #1A1A1A',
          opacity: revealed ? 1 : 0,
          transition: 'opacity 1s ease 0.6s',
        }}
      >
        {/* Scanning line effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF00] to-transparent opacity-30"
            style={{ animation: 'scan 4s linear infinite' }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <img src={ICON_URL} alt="NX" className="h-14 w-14 object-contain flex-shrink-0"
              style={{ filter: 'drop-shadow(0 0 15px rgba(0,255,0,0.5))' }} />
            <div>
              <div className="text-[#00FF00] font-mono text-[10px] uppercase tracking-widest mb-2">Mission Statement</div>
              <p className="text-white text-lg font-bold uppercase leading-tight"
                style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '16px' }}>
                Empower Every Community Bank to Own<br className="hidden sm:block" />
                <span className="text-[#00FF00]"> Their Digital Future</span>
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {[
              { value: '1', label: 'Founder' },
              { value: '∞', label: 'Ambition' },
              { value: '100%', label: 'Bank-Owned' },
            ].map((stat) => (
              <div key={stat.label} className="text-center px-6 border-l border-[#1A1A1A] first:border-l-0 sm:first:border-l-0">
                <div className="text-[#00FF00] font-mono text-3xl font-black"
                  style={{ fontFamily: 'JetBrains Mono, monospace', textShadow: '0 0 20px rgba(0,255,0,0.4)' }}>
                  {stat.value}
                </div>
                <div className="text-[#A3A3A3] text-[10px] uppercase tracking-widest font-mono">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════ ORIGIN TIMELINE ══════════════════════════ */}
      <div
        ref={timelineRef}
        className="max-w-7xl mx-auto px-6 pb-24"
      >
        <div className="text-center mb-14"
          style={{
            opacity: tlRevealed ? 1 : 0,
            transform: tlRevealed ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s ease',
          }}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-[2px] bg-[#00FF00]" />
            <span className="text-[#00FF00] font-mono text-[11px] uppercase tracking-[0.4em]">Origin Story</span>
            <div className="w-10 h-[2px] bg-[#00FF00]" />
          </div>
          <h3 className="text-3xl sm:text-4xl font-black uppercase text-white"
            style={{ fontFamily: 'Unbounded, sans-serif' }}>
            How NexGenPay Was <span className="text-[#00FF00]">Built</span>
          </h3>
        </div>

        {/* Horizontal timeline for desktop / vertical for mobile */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 relative">
          {/* Connecting line (desktop) */}
          <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-[#1A1A1A]" />
          <div
            className="hidden md:block absolute top-8 left-0 h-px bg-[#00FF00] transition-all duration-[2000ms]"
            style={{
              width: tlRevealed ? '100%' : '0%',
              boxShadow: '0 0 8px rgba(0,255,0,0.5)',
              transitionDelay: '0.3s',
            }}
          />

          {timeline.map((item, i) => (
            <div
              key={item.year}
              data-testid={`timeline-item-${i}`}
              className="relative flex flex-col md:items-center gap-4 p-6 md:pt-0 md:px-4"
            >
              {/* Node */}
              <div className="flex md:justify-center mb-4 md:mb-0">
                <div
                  className="relative z-10 w-16 h-16 flex items-center justify-center border-2 font-mono font-black text-sm transition-all duration-500"
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    background: activeYear >= i ? '#00FF00' : '#0A0A0A',
                    borderColor: activeYear >= i ? '#00FF00' : '#1A1A1A',
                    color: activeYear >= i ? '#000' : '#A3A3A3',
                    boxShadow: activeYear >= i ? '0 0 20px rgba(0,255,0,0.4)' : 'none',
                  }}
                >
                  {item.year}
                </div>
              </div>

              <div
                className="md:mt-8 border border-[#1A1A1A] p-4 transition-all duration-500"
                style={{
                  background: activeYear >= i ? '#0A0A0A' : 'transparent',
                  borderColor: activeYear >= i ? 'rgba(0,255,0,0.3)' : '#1A1A1A',
                  opacity: activeYear >= i ? 1 : 0.3,
                }}
              >
                <div className="text-white font-bold text-[11px] uppercase tracking-wide mb-2"
                  style={{ fontFamily: 'Unbounded, sans-serif', lineHeight: '1.4' }}>
                  {item.label}
                </div>
                <p className="text-[#A3A3A3] text-[12px] leading-relaxed"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA row */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-14"
          style={{
            opacity: tlRevealed ? 1 : 0,
            transition: 'opacity 0.7s ease 1.6s',
          }}
        >
          <p className="text-[#A3A3A3] text-sm text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            The product is built. The market is ready. The window is open — but not for long.
          </p>
          <button
            onClick={() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="founder-cta-btn"
            className="flex-shrink-0 btn-neon text-xs flex items-center gap-2"
            style={{ fontFamily: 'Unbounded, sans-serif', padding: '14px 28px' }}
          >
            Talk to Calvin <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #000)' }} />
    </section>
  );
}
